// Loads posthog-js for error tracking only (rules and scrubbing in
// posthog-errors.ts). main.tsx starts it once per page load on the production
// hostname; RouteErrorBoundary reports the render errors it catches, which
// never reach the global handlers. Sentry reports the same errors separately.
import { POSTHOG_API_HOST, POSTHOG_HOSTNAMES, POSTHOG_TOKEN, beforeSend } from "./posthog-errors"

let started = false

function loadPostHog() {
  return import("posthog-js").then(({ default: posthog }) => posthog)
}

export function startPostHogErrors() {
  if (started || !POSTHOG_HOSTNAMES.includes(window.location.hostname)) return
  started = true
  void loadPostHog().then((posthog) =>
    posthog.init(POSTHOG_TOKEN, {
      api_host: POSTHOG_API_HOST,
      defaults: "2026-01-30",
      // Never a person profile, and never identify: one anonymous id per browser.
      person_profiles: "never",
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: false,
      capture_dead_clicks: false,
      capture_heatmaps: false,
      rageclick: false,
      capture_performance: false,
      disable_session_recording: true,
      disable_surveys: true,
      // No feature flags or remote config: the /flags request carries the
      // first URL with its query string and never passes through
      // before_send. With remote config off, the project's exception
      // autocapture toggle never reaches the browser, so it is on here.
      advanced_disable_flags: true,
      // Uncaught errors and unhandled rejections only; console.error carries
      // whatever the page logged.
      capture_exceptions: { capture_unhandled_errors: true, capture_unhandled_rejections: true, capture_console_errors: false },
      before_send: (event) => beforeSend(event),
    }),
  )
}

/** Report an error an error boundary caught. A no-op where PostHog never started. */
export function capturePostHogException(error: unknown, properties?: Record<string, string>) {
  if (!started) return
  void loadPostHog().then((posthog) => posthog.captureException(error, { source: "error-boundary", ...properties }))
}
